import { 
  collection, 
  addDoc, 
  getDocs,
  query, 
  where, 
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  doc,
  updateDoc,
  getDoc
} from 'firebase/firestore'
import { db } from '../firebase'
import config from '../config/environment'

// Development mode flag
const DEV_MODE = config.devMode

// No demo discount logs are shipped. For local dev, use Firestore + emulators.
const mockDiscountLogs = []

export class DiscountService {
  
  /**
   * Log a discount application
   */
  static async logDiscount(discountData) {
    try {
      const logEntry = {
        // Transaction details
        transactionId: discountData.transactionId,
        invoiceId: discountData.invoiceId || null,
        
        // Discount details
        discountType: discountData.type || 'percentage', // percentage, fixed_amount, promotional
        discountPercentage: discountData.percentage || 0,
        discountAmount: discountData.amount,
        originalAmount: discountData.originalAmount,
        finalAmount: discountData.finalAmount,
        reason: discountData.reason || '',
        code: discountData.code || null, // For promotional codes
        
        // Customer details
        customerId: discountData.customerId,
        customerName: discountData.customerName,
        customerEmail: discountData.customerEmail || null,
        customerPhone: discountData.customerPhone || null,
        
        // Staff details
        appliedBy: discountData.appliedBy,
        appliedByName: discountData.appliedByName,
        appliedByRole: discountData.appliedByRole,
        
        // Business details
        branchId: discountData.branchId,
        branchName: discountData.branchName,
        
        // System details
        timestamp: serverTimestamp(),
        date: new Date().toISOString().split('T')[0], // YYYY-MM-DD for easy querying
        hour: new Date().getHours(),
        dayOfWeek: new Date().toLocaleDateString('en-US', { weekday: 'long' }),
        
        // Additional metadata
        metadata: {
          platform: 'POS',
          deviceInfo: navigator.userAgent,
          ipAddress: discountData.ipAddress || null,
          sessionId: discountData.sessionId || null,
          items: discountData.items || [], // Items the discount was applied to
          approved: discountData.approved || false,
          approvedBy: discountData.approvedBy || null,
          flags: discountData.flags || [] // For suspicious activity
        },
        
        // Status and validation
        status: 'active', // active, voided, refunded
        isValid: true,
        voidedAt: null,
        voidedBy: null,
        voidReason: null
      }

      const docRef = await addDoc(collection(db, 'discountLogs'), logEntry)
      
      // Update staff discount statistics
      await this.updateStaffDiscountStats(discountData.appliedBy, discountData.amount, discountData.branchId)
      
      // Update daily discount statistics
      await this.updateDailyDiscountStats(discountData.branchId, discountData.amount)
      
      // Check for unusual discount patterns
      await this.checkDiscountPatterns(discountData.appliedBy, discountData.amount)
      
      return {
        id: docRef.id,
        ...logEntry
      }
    } catch (error) {
      console.error('Error logging discount:', error)
      throw new Error('Failed to log discount')
    }
  }

  /**
   * Get discount logs with filters
   */
  static async getDiscountLogs(filters = {}) {
    try {
      if (DEV_MODE) {
        // Return mock data in development mode
        let filteredLogs = [...mockDiscountLogs]
        
        // Apply filters
        if (filters.branchId) {
          filteredLogs = filteredLogs.filter(log => log.branchId === filters.branchId)
        }
        
        if (filters.appliedBy) {
          filteredLogs = filteredLogs.filter(log => log.appliedBy === filters.appliedBy)
        }
        
        if (filters.customerId) {
          filteredLogs = filteredLogs.filter(log => log.customerId === filters.customerId)
        }
        
        if (filters.date) {
          filteredLogs = filteredLogs.filter(log => log.date === filters.date)
        }
        
        if (filters.dateRange) {
          filteredLogs = filteredLogs.filter(log => 
            log.date >= filters.dateRange.start && log.date <= filters.dateRange.end
          )
        }
        
        if (filters.status) {
          filteredLogs = filteredLogs.filter(log => log.status === filters.status)
        }
        
        if (filters.minAmount) {
          filteredLogs = filteredLogs.filter(log => log.discountAmount >= filters.minAmount)
        }
        
        // Sort by timestamp (newest first)
        filteredLogs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        
        // Apply limit
        if (filters.limit) {
          filteredLogs = filteredLogs.slice(0, filters.limit)
        }
        
        return filteredLogs
      }
      
      let q = collection(db, 'discountLogs')
      
      // Apply filters
      if (filters.branchId) {
        q = query(q, where('branchId', '==', filters.branchId))
      }
      
      if (filters.appliedBy) {
        q = query(q, where('appliedBy', '==', filters.appliedBy))
      }
      
      if (filters.customerId) {
        q = query(q, where('customerId', '==', filters.customerId))
      }
      
      if (filters.date) {
        q = query(q, where('date', '==', filters.date))
      }
      
      if (filters.dateRange) {
        q = query(q, 
          where('date', '>=', filters.dateRange.start),
          where('date', '<=', filters.dateRange.end)
        )
      }
      
      if (filters.status) {
        q = query(q, where('status', '==', filters.status))
      }
      
      if (filters.minAmount) {
        q = query(q, where('discountAmount', '>=', filters.minAmount))
      }
      
      // Add ordering and limits
      q = query(q, orderBy('timestamp', 'desc'))
      
      if (filters.limit) {
        q = query(q, limit(filters.limit))
      }
      
      if (filters.startAfter) {
        q = query(q, startAfter(filters.startAfter))
      }
      
      const snapshot = await getDocs(q)
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate()
      }))
    } catch (error) {
      console.error('Error fetching discount logs:', error)
      throw new Error('Failed to fetch discount logs')
    }
  }

  /**
   * Get discount analytics
   */
  static async getDiscountAnalytics(branchId, dateRange) {
    try {
      if (DEV_MODE) {
        // Return mock analytics in development mode
        const mockLogs = mockDiscountLogs.filter(log => 
          log.branchId === branchId && 
          log.date >= dateRange.start && 
          log.date <= dateRange.end &&
          log.status === 'active'
        )
        
        const analytics = {
          totalDiscounts: mockLogs.length,
          totalDiscountAmount: mockLogs.reduce((sum, log) => sum + log.discountAmount, 0),
          averageDiscount: mockLogs.length > 0 ? mockLogs.reduce((sum, log) => sum + log.discountAmount, 0) / mockLogs.length : 0,
          
          // By staff member
          byStaff: {},
          
          // By discount type
          byType: {},
          
          // By reason
          byReason: {},
          
          // By hour of day
          byHour: {},
          
          // By day of week
          byDayOfWeek: {},
          
          // Large discounts (over R100)
          largeDiscounts: mockLogs.filter(log => log.discountAmount > 100),
          
          // Suspicious patterns
          suspiciousActivity: [],
          
          // Top customers receiving discounts
          topCustomers: {}
        }
        
        // Process analytics
        mockLogs.forEach(log => {
          // By staff
          const staffKey = `${log.appliedByName} (${log.appliedByRole})`
          analytics.byStaff[staffKey] = (analytics.byStaff[staffKey] || 0) + log.discountAmount
          
          // By type
          analytics.byType[log.discountType] = (analytics.byType[log.discountType] || 0) + 1
          
          // By reason
          const reason = log.reason || 'No reason provided'
          analytics.byReason[reason] = (analytics.byReason[reason] || 0) + 1
          
          // By hour
          analytics.byHour[log.hour] = (analytics.byHour[log.hour] || 0) + 1
          
          // By day of week
          analytics.byDayOfWeek[log.dayOfWeek] = (analytics.byDayOfWeek[log.dayOfWeek] || 0) + 1
          
          // Top customers
          analytics.topCustomers[log.customerName] = (analytics.topCustomers[log.customerName] || 0) + log.discountAmount
        })
        
        return analytics
      }
      
      const { start: startDate, end: endDate } = dateRange
      
      const q = query(
        collection(db, 'discountLogs'),
        where('branchId', '==', branchId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        where('status', '==', 'active')
      )
      
      const snapshot = await getDocs(q)
      const logs = snapshot.docs.map(doc => doc.data())
      
      const analytics = {
        totalDiscounts: logs.length,
        totalDiscountAmount: logs.reduce((sum, log) => sum + log.discountAmount, 0),
        averageDiscount: logs.length > 0 ? logs.reduce((sum, log) => sum + log.discountAmount, 0) / logs.length : 0,
        
        // By staff member
        byStaff: {},
        
        // By discount type
        byType: {},
        
        // By reason
        byReason: {},
        
        // By hour of day
        byHour: {},
        
        // By day of week
        byDayOfWeek: {},
        
        // Large discounts (over R100)
        largeDiscounts: logs.filter(log => log.discountAmount > 100),
        
        // Suspicious patterns
        suspiciousActivity: [],
        
        // Top customers receiving discounts
        topCustomers: {}
      }
      
      // Process analytics
      logs.forEach(log => {
        // By staff
        const staffKey = `${log.appliedByName} (${log.appliedByRole})`
        analytics.byStaff[staffKey] = (analytics.byStaff[staffKey] || 0) + log.discountAmount
        
        // By type
        analytics.byType[log.discountType] = (analytics.byType[log.discountType] || 0) + 1
        
        // By reason
        const reason = log.reason || 'No reason provided'
        analytics.byReason[reason] = (analytics.byReason[reason] || 0) + 1
        
        // By hour
        analytics.byHour[log.hour] = (analytics.byHour[log.hour] || 0) + 1
        
        // By day of week
        analytics.byDayOfWeek[log.dayOfWeek] = (analytics.byDayOfWeek[log.dayOfWeek] || 0) + 1
        
        // Top customers
        analytics.topCustomers[log.customerName] = (analytics.topCustomers[log.customerName] || 0) + log.discountAmount
      })
      
      return analytics
    } catch (error) {
      console.error('Error fetching discount analytics:', error)
      throw new Error('Failed to fetch discount analytics')
    }
  }

  /**
   * Void a discount
   */
  static async voidDiscount(discountLogId, voidReason, voidedBy) {
    try {
      const discountRef = doc(db, 'discountLogs', discountLogId)
      
      await updateDoc(discountRef, {
        status: 'voided',
        isValid: false,
        voidedAt: serverTimestamp(),
        voidedBy,
        voidReason
      })
      
      return true
    } catch (error) {
      console.error('Error voiding discount:', error)
      throw new Error('Failed to void discount')
    }
  }

  /**
   * Get staff discount summary
   */
  static async getStaffDiscountSummary(staffId, dateRange) {
    try {
      const { start: startDate, end: endDate } = dateRange
      
      const q = query(
        collection(db, 'discountLogs'),
        where('appliedBy', '==', staffId),
        where('date', '>=', startDate),
        where('date', '<=', endDate),
        where('status', '==', 'active')
      )
      
      const snapshot = await getDocs(q)
      const logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      
      return {
        totalDiscounts: logs.length,
        totalAmount: logs.reduce((sum, log) => sum + log.discountAmount, 0),
        averageDiscount: logs.length > 0 ? logs.reduce((sum, log) => sum + log.discountAmount, 0) / logs.length : 0,
        largestDiscount: logs.length > 0 ? Math.max(...logs.map(log => log.discountAmount)) : 0,
        discountsByDay: logs.reduce((acc, log) => {
          acc[log.date] = (acc[log.date] || 0) + log.discountAmount
          return acc
        }, {}),
        recentDiscounts: logs.slice(0, 10) // Last 10 discounts
      }
    } catch (error) {
      console.error('Error fetching staff discount summary:', error)
      throw new Error('Failed to fetch staff discount summary')
    }
  }

  /**
   * Update staff discount statistics
   */
  static async updateStaffDiscountStats(staffId, discountAmount, branchId) {
    try {
      const today = new Date().toISOString().split('T')[0]
      const staffStatsRef = doc(db, 'staffDiscountStats', `${staffId}-${today}`)
      
      const staffStats = await getDoc(staffStatsRef)
      
      if (staffStats.exists()) {
        await updateDoc(staffStatsRef, {
          totalDiscounts: staffStats.data().totalDiscounts + 1,
          totalAmount: staffStats.data().totalAmount + discountAmount,
          lastDiscount: serverTimestamp()
        })
      } else {
        await addDoc(collection(db, 'staffDiscountStats'), {
          id: `${staffId}-${today}`,
          staffId,
          branchId,
          date: today,
          totalDiscounts: 1,
          totalAmount: discountAmount,
          lastDiscount: serverTimestamp(),
          createdAt: serverTimestamp()
        })
      }
    } catch (error) {
      console.error('Error updating staff discount stats:', error)
    }
  }

  /**
   * Update daily discount statistics
   */
  static async updateDailyDiscountStats(branchId, discountAmount) {
    try {
      const today = new Date().toISOString().split('T')[0]
      const dailyStatsRef = doc(db, 'dailyDiscountStats', `${branchId}-${today}`)
      
      const dailyStats = await getDoc(dailyStatsRef)
      
      if (dailyStats.exists()) {
        await updateDoc(dailyStatsRef, {
          totalDiscounts: dailyStats.data().totalDiscounts + 1,
          totalAmount: dailyStats.data().totalAmount + discountAmount,
          lastDiscount: serverTimestamp()
        })
      } else {
        await addDoc(collection(db, 'dailyDiscountStats'), {
          id: `${branchId}-${today}`,
          branchId,
          date: today,
          totalDiscounts: 1,
          totalAmount: discountAmount,
          lastDiscount: serverTimestamp(),
          createdAt: serverTimestamp()
        })
      }
    } catch (error) {
      console.error('Error updating daily discount stats:', error)
    }
  }

  /**
   * Check for unusual discount patterns
   */
  static async checkDiscountPatterns(staffId, discountAmount) {
    try {
      const today = new Date().toISOString().split('T')[0]
      
      // Check if staff has applied too many discounts today
      const todaysDiscounts = await this.getDiscountLogs({
        appliedBy: staffId,
        date: today,
        status: 'active'
      })
      
      const totalToday = todaysDiscounts.reduce((sum, log) => sum + log.discountAmount, 0)
      
      // Flag if more than R500 in discounts in one day
      if (totalToday > 500) {
        await this.flagSuspiciousActivity({
          type: 'high_daily_discounts',
          staffId,
          amount: totalToday,
          date: today,
          description: `Staff applied R${totalToday.toFixed(2)} in discounts today`
        })
      }
      
      // Flag if single discount is over R200
      if (discountAmount > 200) {
        await this.flagSuspiciousActivity({
          type: 'large_single_discount',
          staffId,
          amount: discountAmount,
          date: today,
          description: `Staff applied large discount of R${discountAmount.toFixed(2)}`
        })
      }
    } catch (error) {
      console.error('Error checking discount patterns:', error)
    }
  }

  /**
   * Flag suspicious activity
   */
  static async flagSuspiciousActivity(activityData) {
    try {
      await addDoc(collection(db, 'suspiciousActivity'), {
        ...activityData,
        timestamp: serverTimestamp(),
        status: 'pending_review',
        reviewedBy: null,
        reviewedAt: null,
        action: null
      })
    } catch (error) {
      console.error('Error flagging suspicious activity:', error)
    }
  }

  /**
   * Export discount logs to CSV
   */
  static async exportDiscountLogs(filters = {}) {
    try {
      const logs = await this.getDiscountLogs(filters)
      
      const csvHeaders = [
        'Date', 'Time', 'Staff', 'Customer', 'Original Amount', 
        'Discount %', 'Discount Amount', 'Final Amount', 'Reason', 
        'Transaction ID', 'Branch', 'Status'
      ]
      
      const csvRows = logs.map(log => [
        log.date,
        log.timestamp?.toLocaleTimeString() || '',
        log.appliedByName,
        log.customerName,
        `R${log.originalAmount.toFixed(2)}`,
        `${log.discountPercentage}%`,
        `R${log.discountAmount.toFixed(2)}`,
        `R${log.finalAmount.toFixed(2)}`,
        log.reason || 'No reason',
        log.transactionId,
        log.branchName,
        log.status
      ])
      
      const csvContent = [csvHeaders, ...csvRows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n')
      
      return csvContent
    } catch (error) {
      console.error('Error exporting discount logs:', error)
      throw new Error('Failed to export discount logs')
    }
  }
}

export const discountService = DiscountService 